import React from 'react';
import { t } from './i18n';

const DashboardItem = ({ icon, title, description, actionText }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm border border-[#e5e7eb] hover:shadow-md transition-shadow h-full">
    <div className="flex items-start gap-3">
      <div className="bg-[#f7f8fa] p-2 rounded-lg flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[#2d348f] font-semibold mb-1 text-sm truncate">{title}</h3>
        <p className="text-gray-500 text-xs mb-2 line-clamp-2">{description}</p>
        <div className="text-[#4962d8] text-xs font-medium truncate">
          {actionText}
        </div>
      </div>
    </div>
  </div>
);

export default function DashboardConfirmationModal({ open, onClose, firstName, onNavigate, lang = "en" }) {
  if (!open) return null;

  const dashboardItems = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M12 15v3m0 0v3m0-3h3m-3 0H9" />
          <circle cx="12" cy="8" r="4" />
        </svg>
      ),
    title: t("account", lang),
      description: t("accountDesc", lang),
      actionText: t("manageAccount", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 7V5z" />
        </svg>
      ),
      title: t("contact", lang),
      description: t("contactDesc", lang),
      actionText: t("manageContacts", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      title: t("profile", lang),
      description: t("profileDesc", lang),
      actionText: t("profileDetails", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M13.73 21a2 2 0 01-3.46 0" />
          <path d="M21 5h-3l-1.5-3h-9L6 5H3a1 1 0 00-1 1v3c0 4.56 2.65 8.58 6.49 10.61a3 3 0 003.02 0C15.35 17.58 18 13.56 18 9V6a1 1 0 00-1-1z" />
        </svg>
      ),
      title: t("lifestyle", lang),
      description: t("lifestyleDesc", lang),
      actionText: t("yourDayToDay", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M3 6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" />
          <path d="M8 2v4M16 2v4M3 10h18" />
        </svg>
      ),
      title: t("doctors", lang),
      description: t("doctorsDesc", lang),
      actionText: t("physiciansAndSpecialists", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z" />
          <path d="M12 8v8M8 12h8" />
        </svg>
      ),
      title: t("hospitals", lang),
      description: t("hospitalsDesc", lang),
      actionText: t("hospitalsAndClinics", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
          <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
        </svg>
      ),
      title: t("documents", lang),
      description: t("documentsDesc", lang),
      actionText: t("manageRecords", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0016.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 002 8.5c0 2.3 1.5 4.05 3 5.5l7 7 7-7z" />
        </svg>
      ),
      title: t("medications", lang),
      description: t("medicationsDesc", lang),
      actionText: t("prescriptionsAndSupplements", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
      ),
      title: t("conditions", lang),
      description: t("conditionsDesc", lang),
      actionText: t("injuriesAndIllnesses", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
        </svg>
      ),
      title: t("procedures", lang),
      description: t("proceduresDesc", lang),
      actionText: t("surgeriesTherapy", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M12 2a3 3 0 00-3 3v7h6V5a3 3 0 00-3-3z" />
          <path d="M19 10h1a2 2 0 012 2v1a2 2 0 01-2 2h-1M5 10H4a2 2 0 00-2 2v1a2 2 0 002 2h1M12 17v3M8 17h8" />
        </svg>
      ),
      title: t("allergies", lang),
      description: t("allergiesDesc", lang),
      actionText: t("allergens", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
        </svg>
      ),
      title: t("history", lang),
      description: t("historyDesc", lang),
      actionText: t("familyMedicalHistory", lang)
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d348f" strokeWidth="2">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 10h18M8 15h8" />
        </svg>
      ),
      title: t("salCard", lang),
      description: t("salCardDesc", lang),
      actionText: t("salMineCard", lang)
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-[#f7f8fa] rounded-2xl shadow-2xl p-6 max-w-5xl w-full max-h-[95vh] overflow-y-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#2d348f] mb-2">
            {t("welcome", lang)}
          </h1>
          <p className="text-gray-600">
            {t("portalDesc", lang)}
          </p>
        </div>

        <h2 className="text-xl font-bold text-[#2d348f] mb-6">
          {t("dashboard", lang)}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {dashboardItems.map((item, index) => (
            <DashboardItem key={index} {...item} />
          ))}
        </div>

        <div className="flex justify-center gap-4">
          <button
            onClick={() => onNavigate('dashboard')}
            className="bg-[#2d348f] text-white font-semibold py-2 px-6 rounded-lg shadow hover:bg-[#4962d8] transition-colors"
          >
            {t("next", lang)}
          </button>
        
        </div>
      </div>
    </div>
  );
}